import React, { useEffect, useRef } from "react";

export interface StreamingTextRendererProps {
  children?: React.ReactNode;
}

export const StreamingTextRenderer = React.memo(
  ({ children }: StreamingTextRendererProps) => {
    const previousTextRef = useRef("");
    const currentText = typeof children === "string" ? children : "";

    useEffect(() => {
      previousTextRef.current = currentText;
    }, [currentText]);

    if (typeof children !== "string") {
      return <>{children}</>;
    }

    const previousText = previousTextRef.current;
    let stableText = "";
    let animatedText = "";

    if (!currentText) {
      stableText = "";
      animatedText = "";
    } else if (!previousText) {
      animatedText = currentText;
    } else if (currentText.startsWith(previousText)) {
      stableText = previousText;
      animatedText = currentText.slice(previousText.length);
    } else {
      // Non-append updates can happen after retries/resets; animate once as one chunk.
      animatedText = currentText;
    }

    if (!animatedText) {
      return <>{stableText}</>;
    }

    const animationKey = `${stableText.length}-${animatedText}`;

    return (
      <>
        {stableText}
        <span
          key={animationKey}
          className="animate-glow-in"
          style={{ display: "inline" }}
        >
          {animatedText}
        </span>
      </>
    );
  },
);

StreamingTextRenderer.displayName = "StreamingTextRenderer";
